import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { EntryCard } from "./components/EntryCard";
import { EntriesTable } from "./components/EntriesTable";
import { useDictionary } from "./hooks/useDictionary";
import type { SortOption, VisibleFields } from "./types";
import "./styles.css";

const INITIAL_VISIBLE = 120;
const FIELD_LABELS: Record<keyof VisibleFields, string> = {
  english: "English",
  definition: "Definition",
  pos: "Part of Speech"
};

function formatRangeLabel(min: number, max: number) {
  return `#${min.toLocaleString()} – #${max.toLocaleString()}`;
}

function App() {
  const { entries, status, error, posList } = useDictionary();
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [selectedPos, setSelectedPos] = useState<Set<string>>(new Set());
  const [rankRange, setRankRange] = useState({ min: 1, max: 5000 });
  const [sortBy, setSortBy] = useState<SortOption>("rank");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [viewMode, setViewMode] = useState<"tiles" | "table">("tiles");
  const [visibleFields, setVisibleFields] = useState<VisibleFields>({
    english: true,
    definition: true,
    pos: true
  });
  const searchInputRef = useRef<HTMLInputElement>(null);

  const koCollator = useMemo(() => new Intl.Collator("ko", { sensitivity: "base" }), []);
  const enCollator = useMemo(() => new Intl.Collator("en", { sensitivity: "base" }), []);

  useEffect(() => {
    if (entries.length > 0) {
      const maxRank = entries[entries.length - 1].rank;
      setRankRange({ min: 1, max: maxRank });
    }
  }, [entries]);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [deferredQuery, selectedPos, rankRange, sortBy]);

  const filteredEntries = useMemo(() => {
    const normalizedQuery = deferredQuery.trim().toLowerCase();
    const hasQuery = normalizedQuery.length > 0;

    const filtered = entries.filter((entry) => {
      const matchesRank = entry.rank >= rankRange.min && entry.rank <= rankRange.max;
      const matchesPos = selectedPos.size === 0 || selectedPos.has(entry.pos);
      const matchesQuery =
        !hasQuery ||
        [entry.term, entry.eng, entry.def]
          .map((value) => (value ?? "").toLowerCase())
          .some((value) => value.includes(normalizedQuery));
      return matchesRank && matchesPos && matchesQuery;
    });

    const sorter = {
      rank: (a: typeof entries[number], b: typeof entries[number]) => a.rank - b.rank,
      korean: (a: typeof entries[number], b: typeof entries[number]) =>
        koCollator.compare(a.term, b.term) || a.rank - b.rank,
      english: (a: typeof entries[number], b: typeof entries[number]) =>
        enCollator.compare(a.eng ?? "", b.eng ?? "") || a.rank - b.rank
    }[sortBy];

    return [...filtered].sort(sorter);
  }, [entries, deferredQuery, enCollator, koCollator, rankRange, selectedPos, sortBy]);

  const visibleEntries = filteredEntries.slice(0, visibleCount);
  const hasMore = filteredEntries.length > visibleCount;

  const togglePos = (pos: string) => {
    setSelectedPos((prev) => {
      const next = new Set(prev);
      next.has(pos) ? next.delete(pos) : next.add(pos);
      return next;
    });
  };

  const resetFilters = () => {
    setSelectedPos(new Set());
    setRankRange((prev) => ({ min: 1, max: prev.max }));
    setSortBy("rank");
    setVisibleCount(INITIAL_VISIBLE);
  };

  const updateRankRange = (key: "min" | "max", value: number) => {
    setRankRange((prev) => {
      const next = { ...prev, [key]: value };
      if (next.min > next.max) {
        key === "min" ? (next.max = next.min) : (next.min = next.max);
      }
      return next;
    });
  };

  const toggleField = (field: keyof VisibleFields) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const exportCsv = () => {
    const columns: Array<{
      key: string;
      label: string;
      include: boolean;
      value: (entry: (typeof entries)[number]) => string;
    }> = [
      { key: "rank", label: "Rank", include: true, value: (entry) => String(entry.rank) },
      { key: "term", label: "Korean", include: true, value: (entry) => entry.term },
      { key: "english", label: "English", include: visibleFields.english, value: (entry) => entry.eng ?? "" },
      { key: "definition", label: "Definition", include: visibleFields.definition, value: (entry) => entry.def ?? "" },
      { key: "pos", label: "POS", include: visibleFields.pos, value: (entry) => entry.pos }
    ].filter((col) => col.include);

    const escapeCsv = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const header = columns.map((col) => col.label).join(",");
    const rows = filteredEntries.map((entry) =>
      columns.map((col) => escapeCsv(col.value(entry))).join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "kimchi-5k.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const applySuggestion = (value: string) => {
    setQuery(value);
    setVisibleCount(INITIAL_VISIBLE);
    searchInputRef.current?.focus();
  };

  return (
    <main className="page">
      <div className="backdrop" aria-hidden="true" />
      <header className="hero">
        <div>
          <p className="eyebrow">Kimchi 5K</p>
          <h1>Search the top 5,000 Korean words</h1>
          <p className="lede">
            Lightweight explorer for the most common Korean terms. Type in Hangul or English, refine
            by part of speech, and browse by frequency rank.
          </p>
          <div className="hero-actions">
            <a
              className="pill soft link"
              href="https://discord.gg/jj7wXhQWJe"
              target="_blank"
              rel="noreferrer"
            >
              Discord for corrections/suggestions
            </a>
            <a
              className="pill soft link"
              href="https://github.com/RickCarlino/kimchi-5k"
              target="_blank"
              rel="noreferrer"
            >
              GitHub repo
            </a>
            {entries.length > 0 && (
              <span className="pill accent">
                {entries.length.toLocaleString()} entries loaded
              </span>
            )}
          </div>
        </div>
        <div className="hero-card">
          <p className="hint">Try searching for:</p>
            <div className="suggestions">
              {["먹다", "생각", "마음", "work", "시간"].map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => applySuggestion(suggestion)}
                  className="suggestion"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
      </header>

      <section className="controls">
        <div className="control-row">
          <label className="field">
            <span className="field-label">Search</span>
            <div className="field-input">
              <span className="icon">⌕</span>
              <input
                ref={searchInputRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Hangul, English, or definition keywords"
              />
            </div>
          </label>
          <div className="field">
            <span className="field-label">Sort</span>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value as SortOption)}>
              <option value="rank">Rank (1 → 5000)</option>
              <option value="korean">Korean (가 → 하)</option>
              <option value="english">English (A → Z)</option>
            </select>
          </div>
          <div className="field view-field">
            <span className="field-label">View</span>
            <div className="segmented">
              <button
                type="button"
                className={viewMode === "tiles" ? "active" : ""}
                onClick={() => setViewMode("tiles")}
              >
                Tiles
              </button>
              <button
                type="button"
                className={viewMode === "table" ? "active" : ""}
                onClick={() => setViewMode("table")}
              >
                Table
              </button>
            </div>
          </div>
        </div>

        <div className="control-row wrap">
          <div className="field stretch">
            <span className="field-label">
              Part of speech
              <button type="button" className="link-button" onClick={() => setSelectedPos(new Set())}>
                Clear
              </button>
            </span>
            <div className="chips">
              {posList.map((pos) => (
                <button
                  key={pos}
                  type="button"
                  className={`chip ${selectedPos.has(pos) ? "selected" : ""}`}
                  onClick={() => togglePos(pos)}
                >
                  {pos}
                </button>
              ))}
              {posList.length === 0 && <span className="hint">Loading parts of speech…</span>}
            </div>
          </div>

          <div className="field range">
            <span className="field-label">Rank window</span>
            <div className="range-inputs">
              <input
                type="range"
                min={1}
                max={entries.length || 5000}
                value={rankRange.min}
                onChange={(event) => updateRankRange("min", Number(event.target.value))}
              />
              <input
                type="range"
                min={1}
                max={entries.length || 5000}
                value={rankRange.max}
                onChange={(event) => updateRankRange("max", Number(event.target.value))}
              />
            </div>
            <div className="range-values">
              <span>{formatRangeLabel(rankRange.min, rankRange.max)}</span>
              <button type="button" className="link-button" onClick={resetFilters}>
                Reset filters
              </button>
            </div>
          </div>
        </div>

        <div className="field toggles">
          <span className="field-label">Show fields</span>
          <div className="toggle-grid">
            {(Object.keys(FIELD_LABELS) as Array<keyof VisibleFields>).map((fieldKey) => (
              <label key={fieldKey} className="toggle">
                <input
                  type="checkbox"
                  checked={visibleFields[fieldKey]}
                  onChange={() => toggleField(fieldKey)}
                />
                <span>{FIELD_LABELS[fieldKey]}</span>
              </label>
            ))}
          </div>
        </div>
      </section>

      <section className="results">
        <div className="results-meta">
          <div>
            <p className="eyebrow">Results</p>
            <h2>
              Showing {visibleEntries.length.toLocaleString()} of{" "}
              {filteredEntries.length.toLocaleString()} matches
            </h2>
            {query && (
              <p className="hint">
                Searching for <strong>{query}</strong>
              </p>
            )}
          </div>
          <div className="summary">
            <span className="pill soft">POS filters: {selectedPos.size || "All"}</span>
            <span className="pill soft">{formatRangeLabel(rankRange.min, rankRange.max)}</span>
            <span className="pill soft">View: {viewMode === "tiles" ? "Tiles" : "Table"}</span>
            <button type="button" className="ghost-button" onClick={exportCsv}>
              Export CSV
            </button>
          </div>
        </div>

        {status === "loading" && (
          <div className="empty">Loading dictionary… this is a one-time download.</div>
        )}

        {status === "error" && <div className="empty error">Error: {error}</div>}

        {status === "ready" && filteredEntries.length === 0 && (
          <div className="empty">No entries match your filters yet.</div>
        )}

        {viewMode === "tiles" ? (
          <div className="grid compact">
            {visibleEntries.map((entry) => (
              <EntryCard
                key={entry.rank}
                entry={entry}
                query={deferredQuery}
                compact
                visibleFields={visibleFields}
              />
            ))}
          </div>
        ) : (
          <EntriesTable
            entries={visibleEntries}
            query={deferredQuery}
            visibleFields={visibleFields}
          />
        )}

        {hasMore && (
          <div className="load-more">
            <button type="button" onClick={() => setVisibleCount((count) => count + INITIAL_VISIBLE)}>
              Load {Math.min(INITIAL_VISIBLE, filteredEntries.length - visibleEntries.length)} more
            </button>
          </div>
        )}
      </section>
    </main>
  );
}

export default App;
